import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { PageLayoutComponent } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { Loader } from '@shapeable/ui';

const MESSAGE_SWAP_MS = 1000;
const MESSAGE_ANIMATION_MS = 550;

const messageOutUp = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }

  to {
    opacity: 0;
    transform: translateY(-18px);
  }
`;

const messageInFromBottom = keyframes`
  from {
    opacity: 0;
    transform: translateY(18px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// -------- Styles -------->

const LayoutStyles = breakpoints({
  base: css`
    align-items: center;
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    min-height: 100vh;
    padding: ${theme.UNIT(6)};
  `,
});

const ContentStyles = breakpoints({
  base: css`
    align-items: center;
    display: flex;
    flex-direction: column;
    font-size: 1.5em;
    gap: ${theme.UNIT(4)};
  `,
});

const MessageStageStyles = breakpoints({
  base: css`
    display: grid;
    justify-items: center;
    min-height: 1.4em;
    overflow: hidden;
    position: relative;
  `,
});

type MessageProps = {
  $phase: 'enter' | 'exit' | 'idle';
};

const MessageStyles = breakpoints({
  base: css`
    grid-area: 1 / 1;
    text-align: center;
    white-space: nowrap;
    will-change: transform, opacity;

    ${({ $phase }: MessageProps) => $phase === 'exit' && css`
      animation: ${messageOutUp} ${MESSAGE_ANIMATION_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
    `}

    ${({ $phase }: MessageProps) => $phase === 'enter' && css`
      animation: ${messageInFromBottom} ${MESSAGE_ANIMATION_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
    `}

    ${({ $phase }: MessageProps) => $phase === 'idle' && css`
      opacity: 1;
      transform: translateY(0);
    `}
  `,
});

// -------- Components -------->

const My = {
  Layout: styled.div`${LayoutStyles}`,
  Content: styled.div`${ContentStyles}`,
  Loader: styled(Loader)`
    flex-direction: column;
  `,
  MessageStage: styled.div`${MessageStageStyles}`,
  Message: styled.div<MessageProps>`${MessageStyles}`,
};

export const PageLayoutSigningIn: PageLayoutComponent = (props) => {
  const { className } = props;
  const [showSigningIn, setShowSigningIn] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowSigningIn(true);
    }, MESSAGE_SWAP_MS);

    return () => clearTimeout(timer);
  }, []);

  return (
    <My.Layout className={className}>
      <My.Content>
        <My.Loader spinnerSize={35}>
          <My.MessageStage>
            <My.Message $phase={showSigningIn ? 'exit' : 'idle'}>
              Hang tight...
            </My.Message>
            {showSigningIn && (
              <My.Message $phase="enter">
                We're signing you in...
              </My.Message>
            )}
          </My.MessageStage>
        </My.Loader>
      </My.Content>
    </My.Layout>
  );
};
